import { useState, useEffect, useRef, useCallback } from "react";
import { supabase, InquiryRow } from "../../../lib/supabase";
import ChatBubble, {
  ChatMsg,
  PdfViewerModal,
  MessageContextMenu,
  useScrollToBottom,
} from "../../../components/feature/ChatMessage";

type Lang = "vi" | "kr";
const T = (lang: Lang, vi: string, kr: string) => (lang === "vi" ? vi : kr);

const STATUS_LABELS: Record<string, { label: string; labelKr: string; color: string }> = {
  new: { label: "Mới", labelKr: "신규", color: "bg-blue-100 text-blue-700" },
  read: { label: "Đã đọc", labelKr: "읽음", color: "bg-gray-100 text-gray-600" },
  replied: { label: "Đã phản hồi", labelKr: "답변 완료", color: "bg-green-100 text-green-700" },
  closed: { label: "Đóng", labelKr: "종료", color: "bg-red-100 text-red-600" },
};

interface Props {
  lang: Lang;
}

const InquiryChatManager = ({ lang }: Props) => {
  const [inquiries, setInquiries] = useState<InquiryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<InquiryRow | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [newMsgAlert, setNewMsgAlert] = useState(false);
  const [attachFile, setAttachFile] = useState<File | null>(null);
  const [attachUploading, setAttachUploading] = useState(false);
  const [pdfViewer, setPdfViewer] = useState<{ url: string; fileName?: string } | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; msg: ChatMsg } | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  // Edit state
  const [editingMsg, setEditingMsg] = useState<ChatMsg | null>(null);
  const [editText, setEditText] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  // Search in chat
  const [chatSearch, setChatSearch] = useState("");
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchResults, setChatSearchResults] = useState<number[]>([]);
  const [chatSearchIdx, setChatSearchIdx] = useState(0);

  // Chat container ref — scroll inside this, NOT the whole page
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const fileAttachRef = useRef<HTMLInputElement>(null);
  const selectedIdRef = useRef<number | undefined>(undefined);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  const msgRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Auto-scroll inside chat container only
  useScrollToBottom(chatContainerRef, [messages.length]);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("inquiries")
      .select("*")
      .order("created_at", { ascending: false });
    setInquiries((data as InquiryRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  // ── Realtime subscription ──
  useEffect(() => {
    const channel = supabase
      .channel("admin-chat-realtime-v4")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "consultation_messages" },
        (payload) => {
          const newMsg = payload.new as ChatMsg;
          if (newMsg.inquiry_id === selectedIdRef.current) {
            setMessages((prev) => {
              const exists = prev.some((m) => m.id === newMsg.id);
              if (exists) return prev;
              return [...prev, newMsg];
            });
            // Scroll inside container only
            setTimeout(() => {
              if (chatContainerRef.current) {
                chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
              }
            }, 100);
          } else if (newMsg.sender_type === "customer") {
            setNewMsgAlert(true);
            fetchInquiries();
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "consultation_messages" },
        (payload) => {
          const updated = payload.new as ChatMsg;
          if (updated.inquiry_id === selectedIdRef.current) {
            setMessages((prev) =>
              prev.map((m) => (m.id === updated.id ? updated : m))
            );
          }
        }
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "consultation_messages" },
        (payload) => {
          const deleted = payload.old as { id?: number };
          if (deleted.id) {
            setMessages((prev) => prev.filter((m) => m.id !== deleted.id));
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [fetchInquiries]);

  // ── Chat search logic ──
  useEffect(() => {
    if (!chatSearch.trim()) {
      setChatSearchResults([]);
      setChatSearchIdx(0);
      return;
    }
    const q = chatSearch.toLowerCase();
    const idxs: number[] = [];
    messages.forEach((msg, i) => {
      if (msg.message?.toLowerCase().includes(q)) idxs.push(i);
    });
    setChatSearchResults(idxs);
    setChatSearchIdx(0);
    if (idxs.length > 0) {
      const msgId = messages[idxs[0]]?.id;
      if (msgId && msgRefs.current[msgId]) {
        msgRefs.current[msgId]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  }, [chatSearch, messages]);

  const navigateSearch = (dir: "prev" | "next") => {
    if (!chatSearchResults.length) return;
    let newIdx = dir === "next"
      ? (chatSearchIdx + 1) % chatSearchResults.length
      : (chatSearchIdx - 1 + chatSearchResults.length) % chatSearchResults.length;
    setChatSearchIdx(newIdx);
    const msgId = messages[chatSearchResults[newIdx]]?.id;
    if (msgId && msgRefs.current[msgId]) {
      msgRefs.current[msgId]?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const openChat = async (inq: InquiryRow) => {
    setSelected(inq);
    selectedIdRef.current = inq.id;
    setReplyText("");
    setEmailSent(false);
    setAttachFile(null);
    setNewMsgAlert(false);
    setEditingMsg(null);
    setChatSearch("");
    setChatSearchOpen(false);

    const { data: msgs } = await supabase
      .from("consultation_messages")
      .select("*")
      .eq("inquiry_id", inq.id)
      .order("created_at", { ascending: true });

    if (!msgs || msgs.length === 0) {
      const { data: inserted } = await supabase
        .from("consultation_messages")
        .insert({
          inquiry_id: inq.id,
          sender_type: "customer",
          sender_name: inq.name,
          message: inq.message,
          created_at: inq.created_at ?? new Date().toISOString(),
        })
        .select()
        .maybeSingle();
      setMessages(inserted ? [inserted as ChatMsg] : [{
        inquiry_id: inq.id!,
        sender_type: "customer",
        sender_name: inq.name,
        message: inq.message,
        created_at: inq.created_at ?? new Date().toISOString(),
      }]);
    } else {
      setMessages(msgs as ChatMsg[]);
    }

    if (inq.status === "new") {
      await supabase.from("inquiries").update({ status: "read" }).eq("id", inq.id);
      fetchInquiries();
    }
    setTimeout(() => {
      if (chatContainerRef.current) {
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
      }
    }, 250);
  };

  // ── Upload attachment ──
  const uploadAttachment = async (file: File): Promise<{ url: string; isPdf: boolean; mimeType: string } | null> => {
    setAttachUploading(true);
    // Keep original extension, sanitize only unsafe chars
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
    const baseName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `chat-attachments/${Date.now()}_${baseName}`;
    const isPdfFile = ext === "pdf" || file.type === "application/pdf";
    // Explicit MIME — do NOT let browser override
    const contentType = isPdfFile
      ? "application/pdf"
      : file.type || "application/octet-stream";

    const { data, error } = await supabase.storage
      .from("bizwell-images")
      .upload(path, file, { upsert: true, contentType, cacheControl: "3600" });

    setAttachUploading(false);
    if (error || !data?.path) {
      console.error("Upload error:", error);
      return null;
    }

    const { data: urlData } = supabase.storage
      .from("bizwell-images")
      .getPublicUrl(data.path);

    if (!urlData?.publicUrl) return null;

    // Verify URL is accessible (quick HEAD check)
    try {
      const check = await fetch(urlData.publicUrl, { method: "HEAD" });
      if (!check.ok) {
        console.warn("File uploaded but URL not accessible:", check.status, urlData.publicUrl);
      }
    } catch { /* non-blocking — proceed anyway */ }

    return { url: urlData.publicUrl, isPdf: isPdfFile, mimeType: contentType };
  };

  const [sendError, setSendError] = useState<string | null>(null);

  const sendReply = async () => {
    if (!selected?.id || (!replyText.trim() && !attachFile)) return;
    setSending(true);
    setSendError(null);

    let attachmentUrl: string | undefined;
    let attachFileName: string | undefined;
    let attachMimeType: string | undefined;
    let isPdfAttach = false;

    if (attachFile) {
      const result = await uploadAttachment(attachFile);
      if (!result) {
        setSendError("Upload file thất bại. Vui lòng thử lại.");
        setSending(false);
        return;
      }
      attachmentUrl = result.url;
      isPdfAttach = result.isPdf;
      attachFileName = attachFile.name;
      attachMimeType = result.mimeType;
    }

    const msgText = replyText.trim()
      || (isPdfAttach ? "Đã gửi một tài liệu PDF" : attachFile ? "Đã gửi một tệp đính kèm" : "Tin nhắn");

    const insertPayload: Record<string, unknown> = {
      inquiry_id: selected.id,
      sender_type: "admin",
      sender_name: "Admin Bizwell",
      message: msgText,
    };
    if (attachmentUrl) {
      insertPayload.attachment_url = attachmentUrl;
      insertPayload.file_url = attachmentUrl;
      insertPayload.file_name = attachFileName;
      insertPayload.file_type = attachMimeType;
    }

    const { error: insertErr } = await supabase
      .from("consultation_messages")
      .insert(insertPayload);

    if (insertErr) {
      setSendError(`Gửi thất bại: ${insertErr.message}`);
      setSending(false);
      return;
    }

    setReplyText("");
    setAttachFile(null);

    // Update inquiries table
    const updatePayload: Record<string, unknown> = {
      reply_message: msgText,
      replied_at: new Date().toISOString(),
      replied_by: "Admin",
      status: "replied",
    };
    if (attachmentUrl) {
      updatePayload.file_url = attachmentUrl;
      updatePayload.file_type = attachMimeType ?? (isPdfAttach ? "application/pdf" : "file");
    }

    await supabase.from("inquiries").update(updatePayload).eq("id", selected.id);
    setSelected((prev) => prev ? { ...prev, status: "replied" } : prev);
    setSending(false);
    fetchInquiries();
  };

  // ── Recall ──
  const handleRecallMsg = useCallback(async (msg: ChatMsg) => {
    if (!msg.id) return;
    await supabase
      .from("consultation_messages")
      .update({ is_recalled: true })
      .eq("id", msg.id);
    setMessages((prev) =>
      prev.map((m) => (m.id === msg.id ? { ...m, is_recalled: true } : m))
    );
  }, []);

  // ── Edit ──
  const startEdit = useCallback((msg: ChatMsg) => {
    setEditingMsg(msg);
    setEditText(msg.message ?? "");
    setTimeout(() => editInputRef.current?.focus(), 100);
  }, []);

  const saveEdit = async () => {
    if (!editingMsg?.id || !editText.trim()) return;
    setSavingEdit(true);
    const now = new Date().toISOString();
    await supabase
      .from("consultation_messages")
      .update({ message: editText.trim(), is_edited: true, edited_at: now })
      .eq("id", editingMsg.id);
    setMessages((prev) =>
      prev.map((m) =>
        m.id === editingMsg.id
          ? { ...m, message: editText.trim(), is_edited: true, edited_at: now }
          : m
      )
    );
    setEditingMsg(null);
    setEditText("");
    setSavingEdit(false);
  };

  const handleCopyMsg = useCallback((msg: ChatMsg) => {
    if (msg.message) {
      navigator.clipboard.writeText(msg.message).catch(() => {});
      setCopiedId(msg.id ?? null);
      setTimeout(() => setCopiedId(null), 2000);
    }
  }, []);

  const handleShareMsg = useCallback((msg: ChatMsg) => {
    const text = msg.message || msg.attachment_url || "";
    if (navigator.share) {
      navigator.share({ text, title: "Tin nhắn từ Bizwell" }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).catch(() => {});
    }
  }, []);

  const handleContextMenu = (e: React.MouseEvent, msg: ChatMsg) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, msg });
  };

  const sendEmailNotification = async () => {
    if (!selected?.email || !selected?.reply_message) return;
    setSendingEmail(true);
    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
      const res = await fetch(`${supabaseUrl}/functions/v1/send-reply-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          customer_name: selected.name,
          customer_email: selected.email,
          product_name: selected.product_name,
          reply_message: selected.reply_message,
        }),
      });
      if (res.ok) setEmailSent(true);
    } catch {
      setEmailSent(false);
    }
    setSendingEmail(false);
  };

  const updateStatus = async (id: number | undefined, status: string) => {
    if (!id) return;
    await supabase.from("inquiries").update({ status }).eq("id", id);
    setSelected((prev) => prev ? { ...prev, status: status as InquiryRow["status"] } : prev);
    fetchInquiries();
  };

  const filtered = inquiries.filter((i) => {
    const matchStatus = statusFilter === "all" || i.status === statusFilter;
    const q = search.toLowerCase();
    const matchSearch = !q || [i.name, i.email, i.product_name].some((s) => s?.toLowerCase().includes(q));
    return matchStatus && matchSearch;
  });

  const stats = {
    new: inquiries.filter(i => i.status === "new").length,
    read: inquiries.filter(i => i.status === "read").length,
    replied: inquiries.filter(i => i.status === "replied").length,
    total: inquiries.length,
  };

  // Unread count for badge (exported via prop callback if needed)
  const unreadCount = stats.new;

  return (
    <div className="flex h-[calc(100vh-140px)] gap-4">
      {/* PDF Viewer Modal */}
      {pdfViewer && (
        <PdfViewerModal url={pdfViewer.url} fileName={pdfViewer.fileName} onClose={() => setPdfViewer(null)} />
      )}

      {/* Context Menu */}
      {contextMenu && (
        <MessageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          msg={contextMenu.msg}
          isOwn={contextMenu.msg.sender_type === "admin"}
          onClose={() => setContextMenu(null)}
          onCopy={() => handleCopyMsg(contextMenu.msg)}
          onRecall={() => handleRecallMsg(contextMenu.msg)}
          onEdit={() => startEdit(contextMenu.msg)}
          onShare={() => handleShareMsg(contextMenu.msg)}
        />
      )}

      {/* Edit Modal */}
      {editingMsg && (
        <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-edit-line text-amber-500" />
                Chỉnh sửa tin nhắn
              </h3>
              <button
                onClick={() => setEditingMsg(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>
            <textarea
              ref={editInputRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              rows={4}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#004A99] resize-none"
            />
            <div className="flex gap-2 mt-4 justify-end">
              <button
                onClick={() => setEditingMsg(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap"
              >
                Hủy
              </button>
              <button
                onClick={saveEdit}
                disabled={savingEdit || !editText.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-amber-600 disabled:opacity-50 whitespace-nowrap"
              >
                {savingEdit ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                Lưu thay đổi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Left panel - list */}
      <div className="w-[360px] flex-shrink-0 flex flex-col">
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            {T(lang, "Yêu Cầu Tư Vấn", "상담 요청")}
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full animate-pulse">
                {unreadCount}
              </span>
            )}
          </h1>
          {newMsgAlert && (
            <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full font-semibold animate-pulse">
              Tin nhắn mới!
            </span>
          )}
        </div>

        {/* Stats mini */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {[
            { key: "total", label: T(lang, "Tổng", "전체"), value: stats.total, color: "text-[#004A99]" },
            { key: "new", label: T(lang, "Mới", "신규"), value: stats.new, color: "text-red-600" },
            { key: "read", label: T(lang, "Đọc", "읽음"), value: stats.read, color: "text-amber-600" },
            { key: "replied", label: T(lang, "Phản hồi", "답변"), value: stats.replied, color: "text-green-600" },
          ].map((s) => (
            <div key={s.key} className={`bg-white rounded-lg border p-2 text-center ${s.key === "new" && s.value > 0 ? "border-red-300 bg-red-50" : "border-gray-200"}`}>
              <p className={`text-lg font-black ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-gray-400">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Search & filter */}
        <div className="space-y-2 mb-3">
          <div className="relative">
            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder={T(lang, "Tìm kiếm...", "검색...")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-xs outline-none focus:border-[#004A99]"
            />
          </div>
          <div className="flex bg-gray-100 rounded-lg p-0.5 text-xs">
            {[
              { value: "all", label: T(lang, "Tất cả", "전체") },
              { value: "new", label: T(lang, "Mới", "신규") },
              { value: "replied", label: T(lang, "Phản hồi", "답변") },
              { value: "closed", label: T(lang, "Đóng", "종료") },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setStatusFilter(opt.value)}
                className={`flex-1 py-1 rounded-md transition-colors cursor-pointer whitespace-nowrap font-medium text-center ${statusFilter === opt.value ? "bg-white text-[#004A99] shadow-sm" : "text-gray-500"}`}
              >
                {opt.label}
                {opt.value === "new" && stats.new > 0 && (
                  <span className="ml-1 inline-flex items-center justify-center w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full">
                    {stats.new}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto space-y-1.5">
          {loading ? (
            <div className="text-center py-10"><i className="ri-loader-4-line text-xl text-[#004A99] animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">
              <i className="ri-mail-open-line text-3xl block mb-2 text-gray-200" />
              {T(lang, "Không có yêu cầu nào", "요청 없음")}
            </div>
          ) : filtered.map((inq) => (
            <button
              key={inq.id}
              onClick={() => openChat(inq)}
              className={`w-full text-left p-3 rounded-xl border transition-all cursor-pointer ${selected?.id === inq.id ? "border-[#004A99] bg-[#EDF2FB]" : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50"} ${inq.status === "new" ? "border-l-[3px] border-l-red-500" : ""}`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="text-sm font-semibold text-gray-900 truncate flex items-center gap-1.5">
                  {inq.status === "new" && <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 animate-pulse" />}
                  {inq.name}
                </p>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0 ${STATUS_LABELS[inq.status ?? "new"]?.color}`}>
                  {lang === "vi" ? STATUS_LABELS[inq.status ?? "new"]?.label : STATUS_LABELS[inq.status ?? "new"]?.labelKr}
                </span>
              </div>
              <p className="text-xs text-[#004A99] truncate">{inq.product_name || "—"}</p>
              <p className="text-[11px] text-gray-400 mt-0.5 line-clamp-2">{inq.message}</p>
              <p className="text-[10px] text-gray-300 mt-1">{inq.created_at?.slice(0, 10)}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Right panel - chat */}
      <div className="flex-1 flex flex-col bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {!selected ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-2xl mb-4">
              <i className="ri-chat-3-line text-3xl text-gray-300" />
            </div>
            <p className="text-gray-500 font-semibold">{T(lang, "Chọn một yêu cầu để xem chi tiết", "요청을 선택해 상세 내용을 보세요")}</p>
            <p className="text-xs text-gray-400 mt-1">{T(lang, "Tất cả lịch sử trao đổi được lưu tự động", "모든 대화 기록이 자동 저장됩니다")}</p>
            {unreadCount > 0 && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-2.5">
                <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
                <p className="text-sm text-red-700 font-semibold">
                  Có <strong>{unreadCount}</strong> yêu cầu mới chưa đọc!
                </p>
              </div>
            )}
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="border-b border-gray-100 px-5 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#EDF2FB] rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-3-line text-[#004A99]" />
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{selected.name}</p>
                  <p className="text-xs text-gray-400">{selected.email} · {selected.phone}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Search in chat toggle */}
                <button
                  onClick={() => { setChatSearchOpen(!chatSearchOpen); if (chatSearchOpen) setChatSearch(""); }}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${chatSearchOpen ? "bg-[#004A99] text-white" : "hover:bg-gray-100 text-gray-500"}`}
                  title="Tìm kiếm trong chat"
                >
                  <i className="ri-search-line text-sm" />
                </button>
                <div className="flex gap-1 flex-wrap">
                  {Object.entries(STATUS_LABELS).map(([key, val]) => (
                    <button
                      key={key}
                      onClick={() => updateStatus(selected.id, key)}
                      className={`px-2.5 py-1 text-[11px] rounded-lg font-semibold cursor-pointer transition-colors whitespace-nowrap ${selected.status === key ? val.color + " ring-1 ring-current" : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
                    >
                      {lang === "vi" ? val.label : val.labelKr}
                    </button>
                  ))}
                </div>
                <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-gray-500 text-sm" />
                </button>
              </div>
            </div>

            {/* Chat search bar */}
            {chatSearchOpen && (
              <div className="px-5 py-2.5 bg-yellow-50 border-b border-yellow-200 flex items-center gap-2">
                <i className="ri-search-line text-yellow-600 text-sm flex-shrink-0" />
                <input
                  type="text"
                  value={chatSearch}
                  onChange={(e) => setChatSearch(e.target.value)}
                  placeholder="Tìm kiếm nội dung trong cuộc trò chuyện..."
                  autoFocus
                  className="flex-1 bg-transparent text-sm outline-none text-gray-800 placeholder-yellow-400"
                />
                {chatSearch && (
                  <span className="text-xs text-yellow-700 font-semibold whitespace-nowrap">
                    {chatSearchResults.length > 0
                      ? `${chatSearchIdx + 1}/${chatSearchResults.length} kết quả`
                      : "Không tìm thấy"}
                  </span>
                )}
                {chatSearchResults.length > 1 && (
                  <>
                    <button onClick={() => navigateSearch("prev")} className="w-6 h-6 flex items-center justify-center rounded hover:bg-yellow-200 cursor-pointer">
                      <i className="ri-arrow-up-s-line text-yellow-700 text-sm" />
                    </button>
                    <button onClick={() => navigateSearch("next")} className="w-6 h-6 flex items-center justify-center rounded hover:bg-yellow-200 cursor-pointer">
                      <i className="ri-arrow-down-s-line text-yellow-700 text-sm" />
                    </button>
                  </>
                )}
                <button onClick={() => { setChatSearch(""); setChatSearchOpen(false); }} className="w-6 h-6 flex items-center justify-center rounded hover:bg-yellow-200 cursor-pointer">
                  <i className="ri-close-line text-yellow-700 text-sm" />
                </button>
              </div>
            )}

            {/* Product badge */}
            {selected.product_name && (
              <div className="px-5 py-2 bg-[#F0F7FF] border-b border-[#004A99]/10 flex items-center gap-2">
                <i className="ri-box-3-line text-[#004A99] text-xs" />
                <p className="text-xs font-semibold text-[#004A99]">{T(lang, "Sản phẩm:", "제품:")} {selected.product_name}</p>
              </div>
            )}

            {/* Messages — scroll inside this container only */}
            <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#FAFBFC]">
              {messages.map((msg, i) => (
                <div
                  key={msg.id ?? i}
                  ref={(el) => { if (msg.id) msgRefs.current[msg.id] = el; }}
                >
                  <ChatBubble
                    msg={msg}
                    isOwn={msg.sender_type === "admin"}
                    isCopied={copiedId === msg.id}
                    onContextMenu={handleContextMenu}
                    onCopy={handleCopyMsg}
                    onOpenContextMenu={handleContextMenu}
                    onPdfPreview={(url, fileName) => setPdfViewer({ url, fileName })}
                    ownerLabel="Admin Bizwell"
                    otherLabel={selected.name}
                    searchQuery={chatSearch}
                  />
                </div>
              ))}
            </div>

            {/* Email notification bar */}
            {selected.status === "replied" && selected.email && (
              <div className="px-5 py-2 bg-green-50 border-t border-green-100 flex items-center justify-between">
                <p className="text-xs text-green-700 flex items-center gap-1.5">
                  <i className="ri-checkbox-circle-line" />
                  {emailSent
                    ? T(lang, "Email đã gửi cho khách hàng!", "고객에게 이메일 발송 완료!")
                    : T(lang, "Đã phản hồi - Gửi email thông báo cho khách hàng?", "답변 완료 - 이메일 알림을 고객에게 보내시겠습니까?")}
                </p>
                {!emailSent && (
                  <button
                    onClick={sendEmailNotification}
                    disabled={sendingEmail}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded-lg cursor-pointer hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
                  >
                    {sendingEmail ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-mail-send-line" />}
                    {T(lang, "Gửi email", "이메일 발송")}
                  </button>
                )}
              </div>
            )}

            {/* Reply input */}
            <div className="border-t border-gray-100 p-4 flex-shrink-0 bg-white">
              {attachFile && (
                <div className="flex items-center gap-2 mb-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                  <i className={`text-xs ${attachFile.type === "application/pdf" || attachFile.name.endsWith(".pdf") ? "ri-file-pdf-2-line text-red-500" : attachFile.type.startsWith("audio/") ? "ri-music-line text-purple-500" : attachFile.type.startsWith("image/") ? "ri-image-line text-green-500" : "ri-attachment-2 text-blue-500"}`} />
                  <span className="text-xs text-blue-700 truncate flex-1">{attachFile.name}</span>
                  <span className="text-[10px] text-blue-400">{(attachFile.size / 1024).toFixed(0)} KB</span>
                  <button onClick={() => setAttachFile(null)} className="cursor-pointer text-blue-400 hover:text-red-500">
                    <i className="ri-close-line text-xs" />
                  </button>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileAttachRef}
                  type="file"
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.mp3,.wav,.ogg,.m4a"
                  onChange={(e) => e.target.files?.[0] && setAttachFile(e.target.files[0])}
                />
                <button
                  onClick={() => fileAttachRef.current?.click()}
                  className="self-end w-9 h-9 flex items-center justify-center border border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer flex-shrink-0"
                  title="Đính kèm file (ảnh, PDF, audio)"
                >
                  <i className="ri-attachment-2 text-gray-400 text-sm" />
                </button>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  rows={3}
                  placeholder={T(lang, "Nhập phản hồi... (Enter để gửi, Shift+Enter xuống dòng)", "답변 입력... (Enter 전송, Shift+Enter 줄바꿈)")}
                  className="flex-1 border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#004A99] resize-none"
                />
                <button
                  onClick={sendReply}
                  disabled={sending || attachUploading || (!replyText.trim() && !attachFile)}
                  className="self-end flex items-center gap-2 px-4 py-2.5 bg-[#004A99] text-white text-sm font-semibold rounded-xl cursor-pointer hover:bg-[#003a7a] disabled:opacity-50 whitespace-nowrap"
                >
                  {(sending || attachUploading) ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-send-plane-line" />}
                  {T(lang, "Gửi", "전송")}
                </button>
              </div>
              {sendError && (
                <div className="mt-2 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-700">
                  <i className="ri-error-warning-line flex-shrink-0" />
                  {sendError}
                  <button onClick={() => setSendError(null)} className="ml-auto cursor-pointer text-red-400 hover:text-red-600">
                    <i className="ri-close-line" />
                  </button>
                </div>
              )}
              <p className="text-[10px] text-gray-400 mt-1.5">
                <i className="ri-information-line mr-1" />
                Chuột phải vào tin nhắn để sao chép, chỉnh sửa, chia sẻ hoặc thu hồi. Hover ảnh để lưu.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InquiryChatManager;
